import React, { useState } from 'react';
import { useUsageStatistics } from '../../api/usage';
import { StepHeading, StepDescription } from '../onboarding/styles';
import { usePleriStatus } from '../onboarding/apis/pleri-status';
import { Button, formatLocale } from '@plerionhq/design-system';
import { Icons } from '../onboarding/components/Icons';
import { UsageChart } from './UsageChart';
import { LoadingSpinner } from '../Pleri/components/ToolExecution/LoadingSpinner';
import { ChartTypeSelector } from './ChartTypeSelector';
import { useChartType } from './useChartType';
import { useIsPleriDebugMode } from '../Pleri/components/ChatArea/MessageActions';

import { useTranslation } from 'react-i18next';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat(formatLocale(), {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatNumber = (num: number) => {
  return new Intl.NumberFormat(formatLocale()).format(num);
};

export const UsagePage: React.FC = () => {
  const { t } = useTranslation('pleri/pages');
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`,
  );

  const [requestsChartType, setRequestsChartType] = useChartType('requests');
  const [costChartType, setCostChartType] = useChartType('cost');

  const { data: pleriStatus } = usePleriStatus();
  const {
    data: usageData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useUsageStatistics(selectedMonth);

  const isPleriDebugMode = useIsPleriDebugMode();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-40">
        <LoadingSpinner text={t('loadingUsageStatistics')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Icons.Warning className="h-8 w-8 text-red-500" />
          <div className="text-red-600">{t('errorLoadingUsageStatistics')}</div>
          <Button
            variant="secondary"
            onClick={() => refetch()}
            label={t('tryAgain')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto -mt-8 max-w-7xl px-4 sm:px-6 lg:px-8">
      {/* Header Section */}
      <div className="mb-2 flex items-center justify-between">
        <div>
          <StepHeading>{t('usageStatistics')}</StepHeading>
          <StepDescription>
            {t('trackYourPleriUsageAndCostsOver')}
          </StepDescription>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="month"
            value={selectedMonth}
            max={`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`}
            onChange={(e) => {
              if (e.target.value) {
                setSelectedMonth(e.target.value);
              } else {
                setSelectedMonth(
                  `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`,
                );
              }
            }}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#FF732E] focus:ring-1 focus:ring-[#FF732E] focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
          />
          <button
            onClick={() => refetch()}
            className="!p-2"
            aria-label={t('refreshData')}
            disabled={isFetching}
          >
            <Icons.Refresh
              className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
            />
          </button>
        </div>
      </div>
      {/* Usage Summary Section */}
      {usageData && pleriStatus?.usage && (
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Premium Requests Card */}
          <div className="rounded-lg border border-gray-100 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-4 text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('premiumRequests')}
            </h3>
            <div className="mb-4 flex flex-col">
              <span className="text-3xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
                {formatNumber(usageData.data.totalPremiumRequests)}
              </span>
            </div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {pleriStatus.usage.premiumRequests.used} /{' '}
                {pleriStatus.usage.premiumRequests.limit}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
              <div
                className="h-full bg-[#FF732E] transition-all"
                style={{
                  width: `${(pleriStatus.usage.premiumRequests.used / pleriStatus.usage.premiumRequests.limit) * 100}%`,
                }}
              />
            </div>
          </div>

          {/* Monthly Cost Card */}
          {isPleriDebugMode && (
            <div className="rounded-lg border border-gray-100 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <h3 className="mb-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('monthlyCost')}
              </h3>
              <div className="mb-4 flex flex-col">
                <span className="text-3xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(usageData.data.totalCost)}
                </span>
              </div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatCurrency(pleriStatus.usage.monthlyCost.used)} /{' '}
                  {formatCurrency(pleriStatus.usage.monthlyCost.limit)}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div
                  className="h-full bg-[#FF732E] transition-all"
                  style={{
                    width: `${(pleriStatus.usage.monthlyCost.used / pleriStatus.usage.monthlyCost.limit) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Total Requests Card */}
          <div className="rounded-lg border border-gray-100 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-4 text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('totalRequests')}
            </h3>
            <div className="mb-4 flex flex-col">
              <span className="text-3xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
                {formatNumber(usageData.data.totalRequests)}
              </span>
            </div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('usedCount', {
                  used: formatNumber(pleriStatus.usage.monthlyRequests.used),
                })}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
              <div
                className="h-full bg-[#FF732E] transition-all"
                style={{
                  width: `${(pleriStatus.usage.monthlyRequests.used / usageData.data.totalRequests) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}
      {/* Usage Charts */}
      {usageData && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Requests Chart */}
          <div className="rounded-lg border border-gray-100 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('dailyRequests')}
              </h3>
              <ChartTypeSelector
                value={requestsChartType}
                onChange={setRequestsChartType}
              />
            </div>
            <UsageChart
              type={requestsChartType}
              data={usageData.data.dailyUsage.map((day) => ({
                date: new Date(day.date).toLocaleDateString(),
                'Non-premium requests': day.totalRequests - day.premiumRequests,
                'Premium requests': day.premiumRequests,
              }))}
              series={['Non-premium requests', 'Premium requests']}
              categoryKey="date"
              showLegend
              seriesProps={{
                stackId: requestsChartType === 'area' ? 'a' : undefined,
              }}
              className="h-[300px]"
            />
          </div>

          {/* Cost Chart */}
          {isPleriDebugMode && (
            <div className="rounded-lg border border-gray-100 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('dailyCost')}
                </h3>
                <ChartTypeSelector
                  value={costChartType}
                  onChange={setCostChartType}
                />
              </div>
              <UsageChart
                type={costChartType}
                data={usageData.data.dailyUsage.map((day) => ({
                  date: new Date(day.date).toLocaleDateString(),
                  Cost: day.totalCost,
                }))}
                series={['Cost']}
                categoryKey="date"
                showLegend
                className="h-[300px]"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
